import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';
import { Question, QuestionnaireResponse } from '../../types';

interface QuestionnaireDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (responses: QuestionnaireResponse) => void;
  questions: Question[];
  title: string;
}

export const QuestionnaireDialog: React.FC<QuestionnaireDialogProps> = ({
  open,
  onClose,
  onSubmit,
  questions,
  title,
}) => {
  const [responses, setResponses] = useState<QuestionnaireResponse>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (questionId: string, value: string | string[] | number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value,
    }));
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    questions.forEach(question => {
      if (question.required && !responses[question.id]) {
        newErrors[question.id] = `${question.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(responses);
      setResponses({});
      setErrors({});
    }
  };

  const handleClose = () => {
    setResponses({});
    setErrors({});
    onClose();
  };

  const renderQuestion = (question: Question) => {
    const value = responses[question.id] || '';
    const error = errors[question.id];

    switch (question.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={question.label}
            value={value}
            onChange={(e) => handleChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            helperText={error || question.helperText}
            error={!!error}
            required={question.required}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={question.label}
            value={value}
            onChange={(e) => handleChange(question.id, Number(e.target.value))}
            placeholder={question.placeholder}
            helperText={error || question.helperText}
            error={!!error}
            required={question.required}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth error={!!error} required={question.required}>
            <FormLabel>{question.label}</FormLabel>
            <Select
              value={value}
              onChange={(e) => handleChange(question.id, e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select an option
              </MenuItem>
              {question.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{error || question.helperText}</FormHelperText>
          </FormControl>
        );

      case 'radio':
        return (
          <FormControl component="fieldset" error={!!error} required={question.required}>
            <FormLabel component="legend">{question.label}</FormLabel>
            <RadioGroup
              value={value}
              onChange={(e) => handleChange(question.id, e.target.value)}
            >
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            <FormHelperText>{error || question.helperText}</FormHelperText>
          </FormControl>
        );

      case 'checkbox':
        const checkboxValues = (value as string[]) || [];
        return (
          <FormControl component="fieldset" error={!!error} required={question.required}>
            <FormLabel component="legend">{question.label}</FormLabel>
            <Box>
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={checkboxValues.includes(option.value)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...checkboxValues, option.value]
                          : checkboxValues.filter(v => v !== option.value);
                        handleChange(question.id, newValues);
                      }}
                    />
                  }
                  label={option.label}
                />
              ))}
            </Box>
            <FormHelperText>{error || question.helperText}</FormHelperText>
          </FormControl>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {questions.map((question) => (
            <Box key={question.id}>
              {renderQuestion(question)}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          // Mobile: stack vertically (column-reverse) so visual order
          // is Add to Cart → Cancel (primary action on top).
          // Desktop: row, default right-aligned.
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );
};
